import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

const resources = {
  "en-CA": {
    translation: {
      app: {
        name: "MapleKey",
        tagline: "Built in Canada. Reimagining property management.",
      },
      nav: {
        dashboard: "Dashboard",
        properties: "Properties",
        units: "Units",
        tenants: "Tenants",
        leases: "Leases",
        maintenance: "Maintenance",
        owners: "Owners",
        reports: "Reports",
        settings: "Settings",
        brain: "Brain",
        openMenu: "Open menu",
        help: "Help",
      },
      maintenance: {
        listTitle: "Maintenance",
        listSubtitle: "Triage and track maintenance requests.",
        newTitle: "New maintenance request",
        newSubtitle: "Add details and photos; AI will triage the issue.",
        requestDetail: "Maintenance request",
        newRequest: "New request",
        new: "New",
        logFirst: "Log your first request",
        emptyTitle: "No maintenance requests yet",
        emptyDescription: "Log an issue with a photo and a short description, and AI will categorize it, estimate urgency, and suggest a trade.",
        disabledTitle: "Maintenance triage is not enabled",
        disabledDescription: "This feature is not currently enabled for your organization.",
        property: "Property",
        selectProperty: "Select a property",
        propertyRequired: "Please choose a property.",
        unit: "Unit (optional)",
        commonAreaOrNoUnit: "Common area / not unit-specific",
        titleLabel: "Title (optional)",
        titlePlaceholder: "Short summary, e.g. Kitchen sink leak",
        description: "Description",
        descriptionPlaceholder: "Describe the issue. What, where, since when.",
        descriptionOrPhotoRequired: "Add a description or at least one photo.",
        photos: "Photos",
        upTo: "up to {{max}}",
        add: "Add",
        addPhoto: "Add photo",
        removePhoto: "Remove photo",
        tooManyPhotos: "You can attach up to {{max}} photos.",
        badPhotoType: "Please use a JPEG, PNG, WEBP, or HEIC image.",
        photoTooLarge: "Each photo must be under 10 MB.",
        submitAndTriage: "Submit and triage",
        cancel: "Cancel",
        back: "Back",
        backToList: "Back to maintenance",
        creatingRequest: "Creating request...",
        uploadingPhoto: "Uploading photo {{n}} of {{total}}...",
        runningTriage: "Running AI triage...",
        working: "Working...",
        triageHint: "Analyzing photos and description. This takes a few seconds.",
        triageFailed: "Triage failed: {{code}}",
        featureDisabled: "Maintenance triage is not enabled for this organization.",
        notAuthenticated: "Not signed in. Please refresh and try again.",
        noOrg: "No active organization. Please refresh.",
        genericError: "Something went wrong. Please try again.",
        notFound: "Request not found.",
        untitled: "Untitled request",
        issue: "Issue",
        noDescription: "No description provided.",
        aiTriage: "AI triage",
        noTriageYet: "Triage has not run for this request yet.",
        safetyFlags: "Safety flags",
        confidence: "Confidence: {{level}}",
        workOrder: "Work order draft",
        workOrderPlaceholder: "The AI-drafted work order appears here. Edit before sending to a trade.",
        saveWorkOrder: "Save work order",
        saved: "Saved",
        unsavedChanges: "Unsaved changes",
        saving: "Saving...",
        statusLabel: "Status",
        photoN: "Photo {{n}}",
        tradePlusTime: "{{trade}} - {{time}}",
        confidenceLevel: { high: "high", medium: "medium", low: "low" },
        urgency: { emergency: "Emergency", urgent: "Urgent", routine: "Routine", low: "Low" },
        status: { new: "New", triaged: "Triaged", scheduled: "Scheduled", in_progress: "In progress", completed: "Completed", cancelled: "Cancelled" },
      },

      brain: {
        title: "Property Brain",
        subtitle: "Ask anything about your portfolio",
        askPlaceholder: "Ask about your portfolio...",
        incompleteNotice: "This response may be incomplete.",
        retry: "Retry",
        dismiss: "Dismiss",
        partialLabel: "Partial response",
        send: "Send",
        cancel: "Cancel",
        newConversation: "New conversation",
        sources: "Sources",
        viewSource: "View",
        emptyHeading: "What would you like to know?",
        emptyHint: "Try one of these to get started:",
        prompt1: "Which units are vacant right now?",
        prompt2: "What is my total monthly rent across all properties?",
        prompt3: "Show me leases ending in the next 90 days",
        prompt4: "List tenants with security deposits I am holding",
        history: "Conversation history",
        deleteConversation: "Delete conversation",
        deleteConfirm: "Delete this conversation? This cannot be undone.",
        thinking: "Thinking...",
        errorTitle: "Could not get a response",
        featureDisabled: "Property Brain is not enabled for this organization.",
        disabled: {
          title: "Property Brain is not active",
          description: "This AI assistant is not currently enabled for your organization. Property Brain learns about your portfolio and answers questions in plain language - from \"which units are vacant\" to \"show me leases ending next month\".",
          contactButton: "Contact us to upgrade",
          contactSubject: "Enable Property Brain for our organization",
          contactBody: "Hello MapleKey team,\n\nI would like to enable Property Brain for our organization.\n\nThank you.",
          learnMore: "Learn more about MapleKey AI",
        },
        historySubtitle: "Browse, resume, or delete past Property Brain conversations.",
        new: "New",
        messageCount_one: "{{count}} message",
        messageCount_other: "{{count}} messages",
        updated: "Updated {{time}}",
        open: "Open",
        deleteConfirmTitle: "Delete this conversation?",
        deleteConfirmDescription: "\"{{title}}\" and all of its messages will be permanently deleted. This cannot be undone.",
        deleting: "Deleting...",
        emptyTitle: "No conversations yet",
        emptyDescription: "Property Brain helps you understand your portfolio. Ask about vacant units, total rent, lease expiries, or anything else.",
        startFirst: "Start your first conversation",
      },
      auth: {
        signIn: "Sign in",
        signUp: "Create account",
        signOut: "Sign out",
        email: "Email",
        password: "Password",
        fullName: "Full name",
        forgotPassword: "Forgot password?",
        magicLink: "Send magic link",
        noAccount: "Don't have an account?",
        haveAccount: "Already have an account?",
      },
      tenantPortal: {
        home: {
          title: "My tenancy",
          greeting: "Hi {{name}}",
          subtitle: "Here are your tenancy details.",
          loading: "Loading your details...",
          loadError: "We could not load your details right now. Please try again later.",
          noLease: "No current lease is on file for your account.",
          noLeaseHelp: "If you believe this is an error, please contact your property manager.",
          reportIssueTitle: "Report an issue",
          reportIssueDesc: "Submit and track maintenance requests.",
          messagesTitle: "Messages",
          messagesDesc: "Notices from your property manager.",
          newBadge_one: "{{count}} new",
          newBadge_other: "{{count}} new",
          unitCardTitle: "My unit",
          unitLabel: "Unit",
          propertyLabel: "Property",
          addressLabel: "Address",
          leaseCardTitle: "My lease",
          startDate: "Start date",
          endDate: "End date",
          term: "Term",
          status: "Status",
          costsCardTitle: "My costs",
          monthlyRent: "Monthly rent",
          rentDue: "Rent due",
          rentDueValue: "Day {{day}} of each month",
          securityDeposit: "Security deposit",
          petDeposit: "Pet deposit",
          lateFee: "Late fee",
          lateFeeValue: "{{fee}} after {{days}} day grace",
          termType: {
            fixed_term: "Fixed term",
            month_to_month: "Month to month",
            week_to_week: "Week to week",
            other: "Other",
          },
          leaseStatus: {
            draft: "Draft",
            pending_signature: "Pending signature",
            active: "Active",
            expired: "Expired",
            terminated: "Terminated",
            renewed: "Renewed",
          },
        },
        notices: {
          title: "Messages",
          subtitle: "Notices from your property manager.",
          newBadge: "New",
          loadError: "We could not load your messages right now. Please try again later.",
          emptyTitle: "You have no messages yet.",
          category: {
            rent_reminder: "Rent reminder",
            rent_overdue: "Overdue rent",
            lease_expiry: "Lease expiry",
            maintenance_update: "Maintenance",
            move_in: "Move-in",
            move_out: "Move-out",
            payment_confirmation: "Payment",
            announcement: "Announcement",
            general: "Notice",
          },
        },
        maintenance: {
          title: "Maintenance",
          subtitle: "Your maintenance requests.",
          report: "Report",
          cardFallback: "Maintenance request",
          submittedBanner: "Your request has been received. We will be in touch.",
          loading: "Loading your requests...",
          emptyTitle: "You have no maintenance requests yet.",
          emptyHint: "Tap Report to tell us about an issue in your unit.",
          photoCount_one: "{{count}} photo",
          photoCount_other: "{{count}} photos",
          status: {
            new: "Submitted",
            triaged: "Received",
            scheduled: "Scheduled",
            in_progress: "In progress",
            completed: "Completed",
            cancelled: "Cancelled",
          },
        },
        maintenanceNew: {
          subtitle: "Tell us what needs fixing in your unit.",
          titlePlaceholder: "e.g. Leaking kitchen tap",
          descriptionLabel: "What is the problem?",
          descriptionPlaceholder: "Describe the issue, where it is, and how long it has been happening.",
          takePhoto: "Take photo",
          choosePhoto: "Choose photo",
          badPhotoType: "That file type is not supported. Use a photo (JPG, PNG, WEBP).",
          photoTooLarge: "That photo is too large (max 10 MB).",
          errorNoAccount: "We could not confirm your account. Please reload and try again.",
          errorNoUnit: "We could not find your unit. Please contact your property manager.",
          errorEmpty: "Please describe the issue or add a photo.",
          dismiss: "Dismiss",
          submitting: "Submitting your request...",
          submittingHint: "This only takes a moment.",
          submit: "Submit request",
          progressCreating: "Creating your request...",
          progressReviewing: "Reviewing your request...",
          uploadFailed: "Photo upload failed: {{error}}",
        },
      },
      ownerPortal: {
        welcome: {
          title: "Welcome to your owner portal",
          subtitle: "We are setting up your account. This only takes a moment.",
        },
        home: {
          title: "My portfolio",
          greeting: "Hi {{name}}",
          subtitle: "Here is how your properties are doing.",
          dashboardSoonTitle: "Dashboard coming soon",
          dashboardSoonHint: "Occupancy, income, and maintenance insights will appear here.",
          summarySoonTitle: "Monthly summary coming soon",
          summarySoonHint: "A plain-language summary of your portfolio, updated monthly.",
        },
        invite: {
          action: "Invite to portal",
          statusNone: "Not invited",
          statusPending: "Invite sent",
          statusActive: "Portal active",
          localePrompt: "Portal language",
          localeEn: "English",
          localeFr: "French",
          sending: "Sending...",
          sent: "Invite sent",
          noEmail: "Add an email to this owner before inviting them.",
          error: "Could not send the invite. Please try again.",
        },
      },

finance: {
        
        tabs: {
          expenses: "Expenses",
          rent: "Rent",
        },
        expenses: {
          title: "Expenses",
          subtitle: "Costs recorded across your portfolio.",
          add: "Add expense",
          error: "Could not load expenses right now.",
          empty: "No expenses recorded yet.",
        },
        rent: {
          title: "Rent",
          statusUncharged: "Not charged",
          statusUnpaid: "Unpaid",
          statusPartial: "Partial",
          statusPaid: "Paid",
          statusCredit: "Credit",
          noTenant: "No primary tenant",
          charged: "Charged",
          paid: "Paid",
          outstanding: "Outstanding",
          chargeFirst: "Generate the charge first",
          recordPayment: "Record payment",
          prevMonth: "Previous month",
          nextMonth: "Next month",
          noArrears: "No tenants in arrears this period",
          remindArrears_one: "Remind {{count}} in arrears",
          remindArrears_other: "Remind {{count}} in arrears",
          generate: "Generate charges",
          error: "Could not load the rent roll right now.",
          empty: "No active leases to show.",
        },
        payment: {
          title: "Record payment",
          subtitle: "Record a rent payment.",
          subtitleNamed: "Payment from {{name}}.",
          charged: "Charged",
          paid: "Paid so far",
          outstanding: "Outstanding",
          amount: "Amount",
          date: "Date",
          reference: "Reference",
          referencePlaceholder: "Cheque / e-transfer #, optional",
          overpayNotice: "This is more than the outstanding balance and will record as a credit.",
          submit: "Record payment",
        },
        addCost: {
          title: "Record a cost",
          subtitleMaintenance: "Log an actual cost against this maintenance request.",
          subtitleStandalone: "Log an expense against a property.",
          property: "Property",
          selectProperty: "Select a property",
          amount: "Amount",
          date: "Date",
          category: "Category",
          capitalClass: "Treatment",
          vendor: "Vendor",
          vendorPlaceholder: "Optional",
          reference: "Invoice / ref",
          referencePlaceholder: "Optional",
          description: "Description",
          descriptionPlaceholder: "Optional",
          submit: "Record cost",
          toastRecorded: "Cost recorded",
          toastError: "Could not record cost: {{error}}",
        },
        txnType: {
          maintenance: "Maintenance",
          repairs: "Repairs",
          landscaping: "Landscaping",
          snow_removal: "Snow removal",
          utilities: "Utilities",
          cleaning: "Cleaning",
          pest_control: "Pest control",
          office: "Office",
          software: "Software",
          advertising: "Advertising",
          professional_fees: "Professional fees",
          mortgage_interest: "Mortgage interest",
          loan_interest: "Loan interest",
          bank_charges: "Bank charges",
          insurance: "Insurance",
          property_tax: "Property tax",
          management_fee: "Management fee",
          capital_improvement: "Capital improvement",
          other_expense: "Other expense",
        },
        capitalClass: {
          operating: "Operating expense",
          capital: "Capital expense",
          depreciable_asset: "Depreciable asset",
        },
      },
      common: {
        cancel: "Cancel",
      },
    },
  },
  "fr-CA": {
    translation: {
      app: {
        name: "MapleKey",
        tagline: "Concu au Canada. Reinventer la gestion immobiliere.",
      },
      nav: {
        dashboard: "Tableau de bord",
        properties: "Proprietes",
        units: "Unites",
        tenants: "Locataires",
        leases: "Baux",
        maintenance: "Entretien",
        owners: "Proprietaires",
        reports: "Rapports",
        settings: "Parametres",
        brain: "Cerveau",
        openMenu: "Ouvrir le menu",
        help: "Aide",
      },
       maintenance: {
        listTitle: "Entretien",
        listSubtitle: "Triez et suivez les demandes d'entretien.",
        newTitle: "Nouvelle demande d'entretien",
        newSubtitle: "Ajoutez des details et des photos; l'IA triera le probleme.",
        requestDetail: "Demande d'entretien",
        newRequest: "Nouvelle demande",
        new: "Nouvelle",
        logFirst: "Enregistrer votre premiere demande",
        emptyTitle: "Aucune demande d'entretien",
        emptyDescription: "Enregistrez un probleme avec une photo et une courte description; l'IA le categorisera, estimera l'urgence et suggerera un corps de metier.",
        disabledTitle: "Le triage d'entretien n'est pas active",
        disabledDescription: "Cette fonctionnalite n'est pas activee pour votre organisation.",
        property: "Propriete",
        selectProperty: "Choisir une propriete",
        propertyRequired: "Veuillez choisir une propriete.",
        unit: "Unite (facultatif)",
        commonAreaOrNoUnit: "Partie commune / non specifique a une unite",
        titleLabel: "Titre (facultatif)",
        titlePlaceholder: "Resume court, p. ex. Fuite sous l'evier",
        description: "Description",
        descriptionPlaceholder: "Decrivez le probleme. Quoi, ou, depuis quand.",
        descriptionOrPhotoRequired: "Ajoutez une description ou au moins une photo.",
        photos: "Photos",
        upTo: "jusqu'a {{max}}",
        add: "Ajouter",
        addPhoto: "Ajouter une photo",
        removePhoto: "Retirer la photo",
        tooManyPhotos: "Vous pouvez joindre jusqu'a {{max}} photos.",
        badPhotoType: "Veuillez utiliser une image JPEG, PNG, WEBP ou HEIC.",
        photoTooLarge: "Chaque photo doit faire moins de 10 Mo.",
        submitAndTriage: "Soumettre et trier",
        cancel: "Annuler",
        back: "Retour",
        backToList: "Retour a l'entretien",
        creatingRequest: "Creation de la demande...",
        uploadingPhoto: "Televersement de la photo {{n}} sur {{total}}...",
        runningTriage: "Triage par l'IA en cours...",
        working: "Traitement...",
        triageHint: "Analyse des photos et de la description. Cela prend quelques secondes.",
        triageFailed: "Echec du triage : {{code}}",
        featureDisabled: "Le triage d'entretien n'est pas active pour cette organisation.",
        notAuthenticated: "Non connecte. Veuillez actualiser et reessayer.",
        noOrg: "Aucune organisation active. Veuillez actualiser.",
        genericError: "Une erreur s'est produite. Veuillez reessayer.",
        notFound: "Demande introuvable.",
        untitled: "Demande sans titre",
        issue: "Probleme",
        noDescription: "Aucune description fournie.",
        aiTriage: "Triage par l'IA",
        noTriageYet: "Le triage n'a pas encore ete execute pour cette demande.",
        safetyFlags: "Alertes de securite",
        confidence: "Confiance : {{level}}",
        workOrder: "Bon de travail (ebauche)",
        workOrderPlaceholder: "Le bon de travail redige par l'IA apparait ici. Modifiez-le avant de l'envoyer a un corps de metier.",
        saveWorkOrder: "Enregistrer le bon de travail",
        saved: "Enregistre",
        unsavedChanges: "Modifications non enregistrees",
        saving: "Enregistrement...",
        statusLabel: "Statut",
        photoN: "Photo {{n}}",
        tradePlusTime: "{{trade}} - {{time}}",
        confidenceLevel: { high: "elevee", medium: "moyenne", low: "faible" },
        urgency: { emergency: "Urgence", urgent: "Urgent", routine: "Routine", low: "Faible" },
        status: { new: "Nouvelle", triaged: "Triee", scheduled: "Planifiee", in_progress: "En cours", completed: "Terminee", cancelled: "Annulee" },
      },

      brain: {
        title: "Cerveau immobilier",
        subtitle: "Posez n'importe quelle question sur votre portefeuille",
        askPlaceholder: "Posez une question sur votre portefeuille...",
        incompleteNotice: "Cette r\u00e9ponse est peut-\u00eatre incompl\u00e8te.",
        retry: "R\u00e9essayer",
        dismiss: "Ignorer",
        partialLabel: "R\u00e9ponse partielle",
        send: "Envoyer",
        cancel: "Annuler",
        newConversation: "Nouvelle conversation",
        sources: "Sources",
        viewSource: "Voir",
        emptyHeading: "Que souhaitez-vous savoir?",
        emptyHint: "Essayez l'une de ces questions pour commencer:",
        prompt1: "Quelles unites sont vacantes en ce moment?",
        prompt2: "Quel est mon loyer mensuel total pour toutes les proprietes?",
        prompt3: "Montrez-moi les baux qui se terminent dans les 90 prochains jours",
        prompt4: "Listez les locataires avec des depots de garantie que je detiens",
        history: "Historique des conversations",
        deleteConversation: "Supprimer la conversation",
        deleteConfirm: "Supprimer cette conversation? Ceci est irreversible.",
        thinking: "Reflexion en cours...",
        errorTitle: "Impossible d'obtenir une reponse",
        featureDisabled: "Le Cerveau immobilier n'est pas active pour cette organisation.",
        disabled: {
          title: "Property Brain n'est pas actif",
          description: "Cet assistant IA n'est pas actuellement active pour votre organisation. Property Brain apprend a connaitre votre portefeuille et repond aux questions en langage clair - de \"quelles unites sont vacantes\" a \"montrez-moi les baux qui se terminent le mois prochain\".",
          contactButton: "Contactez-nous pour activer",
          contactSubject: "Activer Property Brain pour notre organisation",
          contactBody: "Bonjour equipe MapleKey,\n\nJe souhaite activer Property Brain pour notre organisation.\n\nMerci.",
          learnMore: "En savoir plus sur l'IA MapleKey",
        },
        historySubtitle: "Parcourez, reprenez ou supprimez vos conversations Property Brain passees.",
        new: "Nouvelle",
        messageCount_one: "{{count}} message",
        messageCount_other: "{{count}} messages",
        updated: "Mise a jour {{time}}",
        open: "Ouvrir",
        deleteConfirmTitle: "Supprimer cette conversation?",
        deleteConfirmDescription: "\"{{title}}\" et tous ses messages seront supprimes definitivement. Cette action est irreversible.",
        deleting: "Suppression...",
        emptyTitle: "Aucune conversation pour l'instant",
        emptyDescription: "Property Brain vous aide a comprendre votre portefeuille. Posez des questions sur les unites vacantes, le loyer total, les baux qui expirent, ou autre chose.",
        startFirst: "Demarrer votre premiere conversation",
      },
      auth: {
        signIn: "Se connecter",
        signUp: "Creer un compte",
        signOut: "Se deconnecter",
        email: "Courriel",
        password: "Mot de passe",
        fullName: "Nom complet",
        forgotPassword: "Mot de passe oublie?",
        magicLink: "Envoyer un lien magique",
        noAccount: "Vous n'avez pas de compte?",
        haveAccount: "Vous avez deja un compte?",
      },
      tenantPortal: {
        home: {
          title: "Ma location",
          greeting: "Bonjour {{name}}",
          subtitle: "Voici les d\u00e9tails de votre location.",
          loading: "Chargement de vos d\u00e9tails...",
          loadError: "Impossible de charger vos d\u00e9tails pour le moment. Veuillez r\u00e9essayer plus tard.",
          noLease: "Aucun bail en cours n'est associ\u00e9 \u00e0 votre compte.",
          noLeaseHelp: "Si vous pensez qu'il s'agit d'une erreur, veuillez contacter votre gestionnaire immobilier.",
          reportIssueTitle: "Signaler un probl\u00e8me",
          reportIssueDesc: "Soumettez et suivez vos demandes d'entretien.",
          messagesTitle: "Messages",
          messagesDesc: "Avis de votre gestionnaire immobilier.",
          newBadge_one: "{{count}} nouveau",
          newBadge_other: "{{count}} nouveaux",
          unitCardTitle: "Mon unit\u00e9",
          unitLabel: "Unit\u00e9",
          propertyLabel: "Propri\u00e9t\u00e9",
          addressLabel: "Adresse",
          leaseCardTitle: "Mon bail",
          startDate: "Date de d\u00e9but",
          endDate: "Date de fin",
          term: "Dur\u00e9e",
          status: "Statut",
          costsCardTitle: "Mes co\u00fbts",
          monthlyRent: "Loyer mensuel",
          rentDue: "\u00c9ch\u00e9ance du loyer",
          rentDueValue: "Le {{day}} de chaque mois",
          securityDeposit: "D\u00e9p\u00f4t de garantie",
          petDeposit: "D\u00e9p\u00f4t pour animaux",
          lateFee: "Frais de retard",
          lateFeeValue: "{{fee}} apr\u00e8s {{days}} jours de gr\u00e2ce",
          termType: {
            fixed_term: "Dur\u00e9e d\u00e9termin\u00e9e",
            month_to_month: "Mois \u00e0 mois",
            week_to_week: "Semaine \u00e0 semaine",
            other: "Autre",
          },
          leaseStatus: {
            draft: "Brouillon",
            pending_signature: "En attente de signature",
            active: "Actif",
            expired: "Expir\u00e9",
            terminated: "R\u00e9sili\u00e9",
            renewed: "Renouvel\u00e9",
          },
        },
        notices: {
          title: "Messages",
          subtitle: "Avis de votre gestionnaire immobilier.",
          newBadge: "Nouveau",
          loadError: "Impossible de charger vos messages pour le moment. Veuillez r\u00e9essayer plus tard.",
          emptyTitle: "Vous n'avez aucun message pour le moment.",
          category: {
            rent_reminder: "Rappel de loyer",
            rent_overdue: "Loyer en retard",
            lease_expiry: "Expiration du bail",
            maintenance_update: "Entretien",
            move_in: "Emm\u00e9nagement",
            move_out: "D\u00e9m\u00e9nagement",
            payment_confirmation: "Paiement",
            announcement: "Annonce",
            general: "Avis",
          },
        },
        maintenance: {
          title: "Entretien",
          subtitle: "Vos demandes d'entretien.",
          report: "Signaler",
          cardFallback: "Demande d'entretien",
          submittedBanner: "Votre demande a \u00e9t\u00e9 re\u00e7ue. Nous vous contacterons.",
          loading: "Chargement de vos demandes...",
          emptyTitle: "Vous n'avez aucune demande d'entretien pour le moment.",
          emptyHint: "Touchez Signaler pour nous d\u00e9crire un probl\u00e8me dans votre logement.",
          photoCount_one: "{{count}} photo",
          photoCount_other: "{{count}} photos",
          status: {
            new: "Soumise",
            triaged: "Re\u00e7ue",
            scheduled: "Planifi\u00e9e",
            in_progress: "En cours",
            completed: "Termin\u00e9e",
            cancelled: "Annul\u00e9e",
          },
        },
        maintenanceNew: {
          subtitle: "Dites-nous ce qui doit \u00eatre r\u00e9par\u00e9 dans votre logement.",
          titlePlaceholder: "p. ex. Robinet de cuisine qui fuit",
          descriptionLabel: "Quel est le probl\u00e8me?",
          descriptionPlaceholder: "D\u00e9crivez le probl\u00e8me, o\u00f9 il se trouve et depuis combien de temps il dure.",
          takePhoto: "Prendre une photo",
          choosePhoto: "Choisir une photo",
          badPhotoType: "Ce type de fichier n'est pas pris en charge. Utilisez une photo (JPG, PNG, WEBP).",
          photoTooLarge: "Cette photo est trop volumineuse (max. 10 Mo).",
          errorNoAccount: "Nous n'avons pas pu confirmer votre compte. Veuillez recharger la page et r\u00e9essayer.",
          errorNoUnit: "Nous n'avons pas pu trouver votre unit\u00e9. Veuillez contacter votre gestionnaire immobilier.",
          errorEmpty: "Veuillez d\u00e9crire le probl\u00e8me ou ajouter une photo.",
          dismiss: "Ignorer",
          submitting: "Envoi de votre demande...",
          submittingHint: "Cela ne prend qu'un instant.",
          submit: "Envoyer la demande",
          progressCreating: "Cr\u00e9ation de votre demande...",
          progressReviewing: "Examen de votre demande...",
          uploadFailed: "\u00c9chec du t\u00e9l\u00e9versement de la photo : {{error}}",
        },
      },
       ownerPortal: {
        welcome: {
          title: "Bienvenue dans votre portail propri\u00e9taire",
          subtitle: "Nous configurons votre compte. Cela ne prend qu'un instant.",
        },
        home: {
          title: "Mon portefeuille",
          greeting: "Bonjour {{name}}",
          subtitle: "Voici l'\u00e9tat de vos propri\u00e9t\u00e9s.",
          dashboardSoonTitle: "Tableau de bord bient\u00f4t disponible",
          dashboardSoonHint: "Les donn\u00e9es d'occupation, de revenus et d'entretien appara\u00eetront ici.",
          summarySoonTitle: "R\u00e9sum\u00e9 mensuel bient\u00f4t disponible",
          summarySoonHint: "Un r\u00e9sum\u00e9 en langage clair de votre portefeuille, mis \u00e0 jour chaque mois.",
        },
        invite: {
          action: "Inviter au portail",
          statusNone: "Non invit\u00e9",
          statusPending: "Invitation envoy\u00e9e",
          statusActive: "Portail actif",
          localePrompt: "Langue du portail",
          localeEn: "Anglais",
          localeFr: "Fran\u00e7ais",
          sending: "Envoi...",
          sent: "Invitation envoy\u00e9e",
          noEmail: "Ajoutez un courriel \u00e0 ce propri\u00e9taire avant de l'inviter.",
          error: "Impossible d'envoyer l'invitation. Veuillez r\u00e9essayer.",
        },
      },
finance: {
        tabs: {
          expenses: "D\u00e9penses",
          rent: "Loyer",
        },
        expenses: {
          title: "D\u00e9penses",
          subtitle: "Co\u00fbts enregistr\u00e9s dans votre portefeuille.",
          add: "Ajouter une d\u00e9pense",
          error: "Impossible de charger les d\u00e9penses pour le moment.",
          empty: "Aucune d\u00e9pense enregistr\u00e9e pour l'instant.",
        },
        rent: {
          title: "Loyer",
          statusUncharged: "Non factur\u00e9",
          statusUnpaid: "Impay\u00e9",
          statusPartial: "Partiel",
          statusPaid: "Pay\u00e9",
          statusCredit: "Cr\u00e9dit",
          noTenant: "Aucun locataire principal",
          charged: "Factur\u00e9",
          paid: "Pay\u00e9",
          outstanding: "Solde d\u00fb",
          chargeFirst: "G\u00e9n\u00e9rez d'abord la facturation",
          recordPayment: "Enregistrer un paiement",
          prevMonth: "Mois pr\u00e9c\u00e9dent",
          nextMonth: "Mois suivant",
          noArrears: "Aucun locataire en arri\u00e9r\u00e9 pour cette p\u00e9riode",
          remindArrears_one: "Relancer {{count}} en arri\u00e9r\u00e9",
          remindArrears_other: "Relancer {{count}} en arri\u00e9r\u00e9",
          generate: "G\u00e9n\u00e9rer les facturations",
          error: "Impossible de charger le registre des loyers pour le moment.",
          empty: "Aucun bail actif \u00e0 afficher.",
        },
        payment: {
          title: "Enregistrer un paiement",
          subtitle: "Enregistrer un paiement de loyer.",
          subtitleNamed: "Paiement de {{name}}.",
          charged: "Factur\u00e9",
          paid: "Pay\u00e9 \u00e0 ce jour",
          outstanding: "Solde d\u00fb",
          amount: "Montant",
          date: "Date",
          reference: "R\u00e9f\u00e9rence",
          referencePlaceholder: "N\u00b0 de ch\u00e8que / virement, facultatif",
          overpayNotice: "Ce montant d\u00e9passe le solde d\u00fb et sera enregistr\u00e9 comme cr\u00e9dit.",
          submit: "Enregistrer le paiement",
        },
        addCost: {
          title: "Enregistrer un co\u00fbt",
          subtitleMaintenance: "Enregistrez un co\u00fbt r\u00e9el pour cette demande d'entretien.",
          subtitleStandalone: "Enregistrez une d\u00e9pense pour une propri\u00e9t\u00e9.",
          property: "Propri\u00e9t\u00e9",
          selectProperty: "Choisir une propri\u00e9t\u00e9",
          amount: "Montant",
          date: "Date",
          category: "Cat\u00e9gorie",
          capitalClass: "Traitement",
          vendor: "Fournisseur",
          vendorPlaceholder: "Facultatif",
          reference: "Facture / r\u00e9f.",
          referencePlaceholder: "Facultatif",
          description: "Description",
          descriptionPlaceholder: "Facultatif",
          submit: "Enregistrer le co\u00fbt",
          toastRecorded: "Co\u00fbt enregistr\u00e9",
          toastError: "Impossible d'enregistrer le co\u00fbt : {{error}}",
        },
        txnType: {
          maintenance: "Entretien",
          repairs: "R\u00e9parations",
          landscaping: "Am\u00e9nagement paysager",
          snow_removal: "D\u00e9neigement",
          utilities: "Services publics",
          cleaning: "Nettoyage",
          pest_control: "Extermination",
          office: "Bureau",
          software: "Logiciel",
          advertising: "Publicit\u00e9",
          professional_fees: "Honoraires professionnels",
          mortgage_interest: "Int\u00e9r\u00eats hypoth\u00e9caires",
          loan_interest: "Int\u00e9r\u00eats sur pr\u00eat",
          bank_charges: "Frais bancaires",
          insurance: "Assurance",
          property_tax: "Taxes fonci\u00e8res",
          management_fee: "Frais de gestion",
          capital_improvement: "Am\u00e9lioration en capital",
          other_expense: "Autre d\u00e9pense",
        },
        capitalClass: {
          operating: "D\u00e9pense d'exploitation",
          capital: "D\u00e9pense en capital",
          depreciable_asset: "Bien amortissable",
        },
      },
      common: {
        cancel: "Annuler",
      },
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en-CA",
    supportedLngs: ["en-CA", "fr-CA"],
    interpolation: { escapeValue: false },
  });

export default i18n;

export const SUPPORTED_LOCALES = [
  { code: "en-CA", label: "English (Canada)" },
  { code: "fr-CA", label: "Francais (Canada)" },
  { code: "en-US", label: "English (United States)" },
  { code: "en-GB", label: "English (United Kingdom)" },
] as const;
